import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Target, 
  TrendingUp, 
  TrendingDown,
  Scale,
  Handshake,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Play,
  RefreshCw,
  Info,
  DollarSign,
  BarChart3,
  Layers,
  Zap,
  Shield,
  ArrowRight,
  ArrowLeftRight
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

interface GameTheoryResult {
  analysis_id: string;
  optimal_settlement: number;
  settlement_range: {
    floor: number;
    ceiling: number;
    nash_equilibrium: number;
    recommended_target: number;
  };
  zopa: {
    exists: boolean;
    range: number;
    floor: number;
    ceiling: number;
    midpoint: number;
    analysis: string;
  };
  nash_equilibrium: {
    point: number;
    claimant_surplus: number;
    insurer_surplus: number;
    fairness_index: number;
    stability: string;
    explanation: string;
  };
  positions: {
    claimant: {
      opening: number;
      target: number;
      reservation: number;
      batna: number;
      confidence: number;
    };
    insurer: {
      opening: number;
      target: number;
      reservation: number;
      batna: number;
      confidence: number;
    };
  };
  batna_analysis: {
    claimant: {
      value: number;
      strength: string;
      recommendation: string;
    };
    insurer: {
      value: number;
      implication: string;
    };
    relative_power: {
      advantage: string;
      claimant_leverage_score: number;
      negotiation_implication: string;
    };
  };
  concession_strategy: {
    recommended_style: string;
    opening_offer: number;
    target_rounds: number;
    moves: Array<{
      round: number;
      offer: number;
      concession: number;
      concession_pct: number;
      rationale: string;
      expected_counter: number;
      p_accepted: number;
    }>;
    total_concession: number;
    expected_outcome: number;
  };
  recommendations: Array<{
    priority: number;
    category: string;
    title: string;
    recommendation: string;
    rationale: string;
  }>;
  inputs: {
    claim_value: number;
    adjuster_value: number;
    attorney_value: number;
    negotiation_style: string;
  };
}

interface NegotiationStyle {
  id: string;
  name: string;
  description: string;
  best_for: string;
  characteristics: {
    opening_multiplier: number;
    concession_speed: string;
    typical_rounds: number;
  };
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

interface GameTheoryDashboardProps {
  initialClaimValue?: number;
}

export default function GameTheoryDashboard({ initialClaimValue }: GameTheoryDashboardProps = {}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GameTheoryResult | null>(null);
  const [styles, setStyles] = useState<NegotiationStyle[]>([]);
  const [activeTab, setActiveTab] = useState<'input' | 'results' | 'strategy'>('input');

  // Form state
  const [claimValue, setClaimValue] = useState<string>(initialClaimValue ? String(initialClaimValue) : '500000');
  const [adjusterValue, setAdjusterValue] = useState<string>('');
  const [attorneyValue, setAttorneyValue] = useState<string>('');
  const [selectedStyle, setSelectedStyle] = useState<string>('competitive');
  const [useAutoEstimate, setUseAutoEstimate] = useState(true);

  useEffect(() => {
    fetchStyles();
  }, []);

  const fetchStyles = async () => {
    try {
      const data = await api.get('/api/v1/game-theory/styles');
      setStyles(data.styles || []);
    } catch (error) {
      console.error('Error fetching styles:', error);
      // Set default styles if API fails
      setStyles([
        { id: 'aggressive', name: 'Aggressive', description: 'Push for maximum recovery', best_for: 'Strong BATNA cases', characteristics: { opening_multiplier: 1.15, concession_speed: 'slow', typical_rounds: 6 } },
        { id: 'competitive', name: 'Competitive', description: 'Strategic, calculated moves', best_for: 'Standard negotiations', characteristics: { opening_multiplier: 1.05, concession_speed: 'moderate', typical_rounds: 5 } },
        { id: 'cooperative', name: 'Cooperative', description: 'Seek mutual agreement', best_for: 'Ongoing relationships', characteristics: { opening_multiplier: 0.95, concession_speed: 'fast', typical_rounds: 4 } },
        { id: 'accommodating', name: 'Accommodating', description: 'Quick settlement', best_for: 'Time-sensitive cases', characteristics: { opening_multiplier: 0.90, concession_speed: 'very_fast', typical_rounds: 3 } }
      ]);
    }
  };

  const runAnalysis = async () => {
    const claim = parseFloat(claimValue);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }

    setLoading(true);
    try {
      let data;
      if (useAutoEstimate) {
        data = await api.post('/api/v1/game-theory/quick-analyze', {
          claim_value: claim,
          negotiation_style: selectedStyle
        });
      } else {
        const adjuster = parseFloat(adjusterValue) || claim * 0.75;
        const attorney = parseFloat(attorneyValue) || claim * 1.25;
        data = await api.post('/api/v1/game-theory/analyze', {
          claim_value: claim,
          adjuster_value: adjuster,
          attorney_value: attorney,
          negotiation_style: selectedStyle
        });
      }
      setResult(data);
      setActiveTab('results');
      toast.success('Analysis complete!');
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getStabilityColor = (stability: string) => {
    switch (stability) {
      case 'highly_stable': return 'text-green-600 bg-green-100';
      case 'stable': return 'text-blue-600 bg-blue-100';
      case 'moderately_stable': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-red-600 bg-red-100';
    }
  };

  const getAdvantageColor = (advantage: string) => {
    switch (advantage) {
      case 'claimant': return 'text-green-600';
      case 'balanced': return 'text-blue-600';
      case 'insurer': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Game Theory Analysis</h1>
            <p className="text-gray-500">Strategic negotiation intelligence for optimal settlements</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[
          { id: 'input', label: 'Analysis Input', icon: Target },
          { id: 'results', label: 'Results', icon: BarChart3, disabled: !result },
          { id: 'strategy', label: 'Concession Strategy', icon: TrendingUp, disabled: !result },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id as any)}
            disabled={tab.disabled}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-blue-600 border-blue-600'
                : tab.disabled
                ? 'text-gray-300 border-transparent cursor-not-allowed'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Input Tab */}
      {activeTab === 'input' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Form */}
          <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Claim Values
            </h3>

            <div className="space-y-4">
              {/* Claim Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base Claim Value *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={claimValue}
                    onChange={(e) => setClaimValue(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="500000"
                  />
                </div>
              </div>

              {/* Auto-estimate toggle */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="autoEstimate"
                  checked={useAutoEstimate}
                  onChange={(e) => setUseAutoEstimate(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="autoEstimate" className="text-sm text-gray-700">
                  Auto-estimate adjuster & attorney values (75% / 125% of claim)
                </label>
              </div>

              {/* Manual Values */}
              {!useAutoEstimate && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adjuster Value
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={adjusterValue}
                        onChange={(e) => setAdjusterValue(e.target.value)}
                        className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={String(parseFloat(claimValue) * 0.75 || '')}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Attorney Value
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={attorneyValue}
                        onChange={(e) => setAttorneyValue(e.target.value)}
                        className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={String(parseFloat(claimValue) * 1.25 || '')}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Negotiation Style */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Negotiation Style
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {styles.map(style => (
                    <button
                      key={style.id}
                      onClick={() => setSelectedStyle(style.id)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        selectedStyle === style.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-medium text-gray-900">{style.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{style.characteristics.typical_rounds} rounds</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Run Button */}
              <button
                onClick={runAnalysis}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all font-medium"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
                {loading ? 'Analyzing...' : 'Run Game Theory Analysis'}
              </button>
            </div>
          </div>

          {/* Style Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              Style Guide
            </h3>
            {styles.find(s => s.id === selectedStyle) && (
              <div className="space-y-4">
                <div>
                  <p className="font-medium text-gray-900">
                    {styles.find(s => s.id === selectedStyle)?.name}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {styles.find(s => s.id === selectedStyle)?.description}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">Best For:</p>
                  <p className="text-sm text-blue-700 mt-1">
                    {styles.find(s => s.id === selectedStyle)?.best_for}
                  </p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Opening Multiplier</span>
                    <span className="font-medium">
                      {((styles.find(s => s.id === selectedStyle)?.characteristics.opening_multiplier || 1) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Concession Speed</span>
                    <span className="font-medium capitalize">
                      {styles.find(s => s.id === selectedStyle)?.characteristics.concession_speed}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Typical Rounds</span>
                    <span className="font-medium">
                      {styles.find(s => s.id === selectedStyle)?.characteristics.typical_rounds}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && result && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 opacity-80" />
                <span className="text-sm opacity-80">Optimal Settlement</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(result.optimal_settlement)}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-5 h-5 opacity-80" />
                <span className="text-sm opacity-80">Nash Equilibrium</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(result.nash_equilibrium.point)}</p>
            </div>
            <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl p-5 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-5 h-5 opacity-80" />
                <span className="text-sm opacity-80">ZOPA Range</span>
              </div>
              <p className="text-2xl font-bold">
                {result.zopa.exists ? formatCurrency(result.zopa.range) : 'No ZOPA'}
              </p>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl p-5 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Handshake className="w-5 h-5 opacity-80" />
                <span className="text-sm opacity-80">Expected Outcome</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(result.concession_strategy.expected_outcome)}</p>
            </div>
          </div>

          {/* ZOPA Visualization */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-blue-600" />
              Zone of Possible Agreement (ZOPA)
            </h3>
            
            {result.zopa.exists ? (
              <div className="space-y-4">
                <div className="relative h-16 bg-gray-100 rounded-lg overflow-hidden">
                  {/* ZOPA Zone */}
                  <div 
                    className="absolute h-full bg-gradient-to-r from-green-200 to-green-300"
                    style={{
                      left: `${((result.zopa.floor - result.inputs.adjuster_value * 0.7) / (result.inputs.attorney_value * 1.15 - result.inputs.adjuster_value * 0.7)) * 100}%`,
                      width: `${(result.zopa.range / (result.inputs.attorney_value * 1.15 - result.inputs.adjuster_value * 0.7)) * 100}%`
                    }}
                  />
                  {/* Nash Point Marker */}
                  <div 
                    className="absolute top-0 h-full w-1 bg-blue-600"
                    style={{
                      left: `${((result.nash_equilibrium.point - result.inputs.adjuster_value * 0.7) / (result.inputs.attorney_value * 1.15 - result.inputs.adjuster_value * 0.7)) * 100}%`
                    }}
                  >
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium text-blue-600 whitespace-nowrap">
                      Nash: {formatCurrency(result.nash_equilibrium.point)}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <div className="text-left">
                    <p className="text-gray-500">ZOPA Floor</p>
                    <p className="font-semibold text-gray-900">{formatCurrency(result.zopa.floor)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Midpoint</p>
                    <p className="font-semibold text-green-600">{formatCurrency(result.zopa.midpoint)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500">ZOPA Ceiling</p>
                    <p className="font-semibold text-gray-900">{formatCurrency(result.zopa.ceiling)}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{result.zopa.analysis}</p>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg text-red-700">
                <AlertTriangle className="w-5 h-5" />
                <p>{result.zopa.analysis}</p>
              </div>
            )}
          </div>

          {/* Nash Equilibrium & BATNA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Nash Equilibrium */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-blue-600" />
                Nash Equilibrium
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Claimant Surplus</p>
                    <p className="text-xl font-bold text-green-700">
                      {formatCurrency(result.nash_equilibrium.claimant_surplus)}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600">Insurer Surplus</p>
                    <p className="text-xl font-bold text-blue-700">
                      {formatCurrency(result.nash_equilibrium.insurer_surplus)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Stability</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStabilityColor(result.nash_equilibrium.stability)}`}>
                    {result.nash_equilibrium.stability.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Fairness Index</span>
                  <span className="font-medium">{(result.nash_equilibrium.fairness_index * 100).toFixed(0)}%</span>
                </div>
                <p className="text-sm text-gray-600">{result.nash_equilibrium.explanation}</p>
              </div>
            </div>

            {/* BATNA Analysis */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                BATNA Analysis
              </h3>
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-600">Your BATNA Value</span>
                    <span className="font-bold text-blue-700">
                      {formatCurrency(result.batna_analysis.claimant.value)}
                    </span>
                  </div>
                  <p className="text-sm text-blue-600 mt-2">
                    Strength: <span className="font-medium capitalize">{result.batna_analysis.claimant.strength}</span>
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Power Advantage</span>
                    <span className={`font-bold capitalize ${getAdvantageColor(result.batna_analysis.relative_power.advantage)}`}>
                      {result.batna_analysis.relative_power.advantage}
                    </span>
                  </div>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                      style={{ width: `${result.batna_analysis.relative_power.claimant_leverage_score * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Leverage Score: {(result.batna_analysis.relative_power.claimant_leverage_score * 100).toFixed(0)}%
                  </p>
                </div>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {result.batna_analysis.relative_power.negotiation_implication}
                </p>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-600" />
              Strategic Recommendations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {result.recommendations.map((rec, idx) => (
                <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:border-blue-200 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-bold text-sm">{rec.priority}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{rec.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{rec.recommendation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Strategy Tab */}
      {activeTab === 'strategy' && result && (
        <div className="space-y-6">
          {/* Strategy Overview */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Concession Strategy ({result.concession_strategy.recommended_style})
            </h3>
            
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600">Opening Offer</p>
                <p className="text-lg font-bold text-blue-700">
                  {formatCurrency(result.concession_strategy.opening_offer)}
                </p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600">Target Rounds</p>
                <p className="text-lg font-bold text-green-700">
                  {result.concession_strategy.target_rounds}
                </p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-600">Total Concession</p>
                <p className="text-lg font-bold text-orange-700">
                  {formatCurrency(result.concession_strategy.total_concession)}
                </p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Expected Outcome</p>
                <p className="text-lg font-bold text-gray-700">
                  {formatCurrency(result.concession_strategy.expected_outcome)}
                </p>
              </div>
            </div>

            {/* Concession Timeline */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Recommended Offer Sequence</h4>
              <div className="relative">
                {result.concession_strategy.moves.map((move, idx) => (
                  <div key={idx} className="flex items-start gap-4 mb-4 last:mb-0">
                    {/* Timeline Line */}
                    <div className="relative">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        idx === 0 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {move.round}
                      </div>
                      {idx < result.concession_strategy.moves.length - 1 && (
                        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-0.5 h-16 bg-gray-200" />
                      )}
                    </div>
                    
                    {/* Move Details */}
                    <div className="flex-1 bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">
                          Your Offer: {formatCurrency(move.offer)}
                        </span>
                        <span className="text-sm text-red-600">
                          -{move.concession_pct}% concession
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <ArrowRight className="w-4 h-4" />
                        <span>Expected Counter: {formatCurrency(move.expected_counter)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{move.rationale}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          move.p_accepted > 0.7 ? 'bg-green-100 text-green-700' :
                          move.p_accepted > 0.4 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {(move.p_accepted * 100).toFixed(0)}% likely accepted
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Position Comparison */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Position Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Position</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-green-600">Claimant</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-blue-600">Insurer</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-700">Opening Position</td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">
                      {formatCurrency(result.positions.claimant.opening)}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-blue-600">
                      {formatCurrency(result.positions.insurer.opening)}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-700">Target Position</td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">
                      {formatCurrency(result.positions.claimant.target)}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-blue-600">
                      {formatCurrency(result.positions.insurer.target)}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-700">Reservation Price</td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">
                      {formatCurrency(result.positions.claimant.reservation)}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-blue-600">
                      {formatCurrency(result.positions.insurer.reservation)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">BATNA Value</td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">
                      {formatCurrency(result.positions.claimant.batna)}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-blue-600">
                      {formatCurrency(result.positions.insurer.batna)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
